import {
  ProjectStatusType,
  useFetchQuotationUserChatRoomsLazyQuery,
  useFetchQuotationUserChatRoomsQuery
} from '@generated/graphql';
import useBrowserVisiblity from '@hooks/useBrowserVisiblity';
import useIntersectionObserver from '@hooks/useIntersectionObserver';
import { useSelector } from '@store';
import { useCallback, useState } from 'react';

const LIMIT = 30;

export interface SearchParam {
  keyword?: string;
  searchType?: ProjectStatusType;
}

const useChatList = () => {
  const accessToken = useSelector((state) => state.client.accessToken);
  const [page, setPage] = useState(1);
  const [searchParam, setSearchParam] = useState<SearchParam>({});
  const listSize = page * LIMIT;

  const {
    data,
    loading: fetchLoading,
    refetch,
    fetchMore
  } = useFetchQuotationUserChatRoomsQuery({
    skip: !accessToken,
    fetchPolicy: 'network-only',
    variables: {
      input: {
        searchKeyword: searchParam.keyword,
        limit: LIMIT,
        searchStatusType: searchParam.searchType,
        offset: 0
      }
    }
  });

  const [, listRefresh] = useFetchQuotationUserChatRoomsLazyQuery({
    variables: {
      input: {
        searchKeyword: searchParam.keyword,
        searchStatusType: searchParam.searchType,
        limit: listSize,
        offset: 0
      }
    },
    fetchPolicy: 'network-only'
  });

  const chatRooms = data?.fetchQuotationUserChatRooms.data?.chatRooms || [];
  const count =
    data?.fetchQuotationUserChatRooms.data?.currentChatRoomCount ?? 0;
  const totalPage = Math.ceil(count / LIMIT);

  const intersectHandler = () => {
    if (totalPage > page) {
      setPage((prev) => prev + 1);
    }

    fetchMore({
      variables: {
        input: {
          searchKeyword: searchParam.keyword,
          searchStatusType: searchParam.searchType,
          limit: LIMIT,
          offset: listSize
        }
      }
    });
  };

  useBrowserVisiblity({
    onVisible: () => {
      listRefresh.refetch();
      listRefresh.startPolling(3000);
    },
    onInVisible: listRefresh.stopPolling
  });

  const { callbackRef } = useIntersectionObserver<HTMLDivElement>({
    callback: intersectHandler
  });

  const refetchHandler = useCallback(() => {
    return refetch({
      input: {
        searchKeyword: searchParam.keyword,
        searchStatusType: searchParam.searchType,
        limit: page * LIMIT,
        offset: 0
      }
    });
  }, [page, searchParam, refetch]);

  return {
    chatRooms,
    fetchLoading,
    callbackRef,
    setSearchParam,
    refetch: refetchHandler
  };
};

export default useChatList;
